import { ApolloClient, createHttpLink, InMemoryCache, isApolloError, split } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { getOperationAST } from 'graphql';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import io from 'socket.io-client';
import Cookies from 'universal-cookie';

import { evCompareCacheListing } from '../assets/javascripts/function';

import { expiredUser } from '../pages/users/partials/logout';

export const forstokCookies = new Cookies();

const isStatic = window.location.pathname === '/static/prints';

let websocketRetryAttempt = 0;

const getToken = () => {
  const urlParams = new URLSearchParams(window.location.search),
    tokenParams = urlParams.get('token') || '';
  const Retoken = isStatic ? tokenParams || '' : forstokCookies.get('token');
  return Retoken;
};

const link = createHttpLink({ uri: process.env.REACT_APP_GRAPHQL_URL });

const wsClient = createClient({
  url: process.env.REACT_APP_SUBSCRIPTION_URL,
  lazy: true,
  retryAttempts: Infinity,
  connectionParams: () => {
    const token = getToken();
    if (!token) {
      void expiredUser();
    }

    return {
      authorization: token ? `Bearer ${token}` : '',
    };
  },
  shouldRetry: () => {
    websocketRetryAttempt += 1;
    console.log(`websocket retry attempt ${websocketRetryAttempt}`);
    return true;
  },
  on: {
    connected: (socket) => {
      websocketRetryAttempt = 0;
      const activeSocket = socket;
      console.log('websocket connected!!', { protocol: activeSocket.protocol || null });
    },
    closed: (event) => {
      const closeEvent = event;
      console.log('websocket closed!!', {
        code: closeEvent.code,
        reason: closeEvent.reason,
        wasClean: closeEvent.wasClean,
        willRetry: true,
      });
    },
    error: (error) => {
      console.log('websocket error!!', error);
    },
  },
});
const wsLink = new GraphQLWsLink(wsClient);

const forstokHttpLink = link;
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  forstokHttpLink,
);
const forstokAuthLink = setContext((_, { headers }) => {
  const token = getToken();
  if (!token) {
    void expiredUser();
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const errorLogLink = onError(({ graphQLErrors, networkError, operation }) => {
  const operationType = getOperationAST(operation.query, operation.operationName || undefined)?.operation;
  // GraphQLWsLink wraps subscription operation errors in ApolloError.
  const subscriptionError =
    operationType === 'subscription' && networkError && isApolloError(networkError) ? networkError : undefined;
  const errors = graphQLErrors ?? subscriptionError?.graphQLErrors ?? [];

  for (const error of errors) {
    const code = error.extensions?.code;
    if (code === 'UNAUTHENTICATED') {
      void expiredUser();
    }
  }
});

export const cache = new InMemoryCache({
  // cacheRedirects: {
  //   Query: {
  //     order: (_, { id }) =>
  //       toIdValue(cache.config.dataIdFromObject({ __typename: 'Order', id })),
  //     package: (_, { id }) =>
  //       toIdValue(cache.config.dataIdFromObject({ __typename: 'Package', id })),
  //     picklist: (_, { id }) =>
  //       toIdValue(cache.config.dataIdFromObject({ __typename: 'Picklist', id })),
  //     fulfillment: (_, { id }) =>
  //       toIdValue(cache.config.dataIdFromObject({ __typename: 'OrderFulfillment', id })),
  //     invoice: (_, { id }) =>
  //       toIdValue(cache.config.dataIdFromObject({ __typename: 'OrderInvoice', id })),
  //     paymentReceiveds: (_, { id }) =>
  //       toIdValue(cache.config.dataIdFromObject({ __typename: 'PaymentReceived', id })),
  //     products: (_, { id }) =>
  //       toIdValue(cache.config.dataIdFromObject({ __typename: 'Product', id })),
  //     listings: (_, { id }) =>
  //       toIdValue(cache.config.dataIdFromObject({ __typename: 'ProductListing', id })),
  //     return: (_, { id }) =>
  //       toIdValue(cache.config.dataIdFromObject({__typename: 'Return', id}))
  //   }
  // },
  typePolicies: {
    Query: {
      fields: {
        warehouses: {
          merge(existing, incoming) {
            return incoming;
          },
        },
        orders: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        packages: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        picklists: {
          cancelReasons: {
            merge(existing, incoming) {
              return { ...existing, ...incoming };
            },
          },
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        fulfillments: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        profileActivities: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        invoices: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        paymentReceiveds: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        products: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        variants: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        listings: {
          merge(existing, incoming, { args, readField }) {
            return evCompareCacheListing(existing, incoming, args, readField) ? { ...existing, ...incoming } : existing;
          },
        },
        listingBrands: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        categoryMappings: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        financeTransactions: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        qtyInventories: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        GetProductBundles: {
          merge(existing, incoming) {
            return incoming;
          },
        },
        promotions: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        listingTotalSkus: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockHistoryList: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockInbounds: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        priceAdjustments: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        priceHistories: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        returnOrders: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        inventoryForecasting: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        productarchiveds: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockAdjustments: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockOutbounds: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        reportingDashboard: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockTransfers: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        getChannelWarehouses: {
          merge(existing, incoming) {
            return incoming;
          },
        },
        contactCompanies: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        contacts: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        contactOrders: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        contactAttributes: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        ShippingReporting: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
      },
    },
    Order: {
      fields: {
        // These fields are complete snapshots, so incoming arrays should replace
        // previously cached values instead of being combined with stale entries.
        itemLines: {
          merge: false,
        },
        cancelReasons: {
          merge: false,
        },
      },
    },
    Warehouses: {
      keyFields: ['id'],
    },
    Accounts: {
      keyFields: ['id', ['name']],
    },
    WarehouseInventoryPromotion: {
      keyFields: false,
    },
    WarehouseInventoryReservedOrder: {
      keyFields: false,
    },
    Bin: {
      keyFields: false,
    },
    Channels: {
      keyFields: ['navbarId'],
    },
    InputGetVariantWarehousePayload: {
      keyFields: false,
    },
  },
  addTypename: true,
});

export const client = new ApolloClient({
  link: errorLogLink.concat(forstokAuthLink).concat(splitLink),
  cache,
  queryDeduplication: false,
});

export const socket = io.connect('wss://socket.forstok.com');
